// "Crear con IA" desde la planificación y las referencias de un post —
// ADR-2026-10-08-crear-con-ia-va-a-la-seccion-del-agente (supersede la apertura en el panel de
// ADR-2026-10-08-crear-con-ia-lleva-el-brief-al-chat).
//
// La planificación y las referencias son el brief del post. Este botón lleva al usuario a la
// sección del Agente (`/agente`, la pantalla completa del chat) con las dos ya escritas en la
// caja, para que las ajuste y cree las imágenes ahí. No se manda solo: el turno es del
// usuario. Antes de irse, el post se guarda — si no, lo que escribió en la planificación se
// perdía con la navegación.
//
// Aislado porque `useChatAtlas` se re-renderiza con cada token.

import { useState } from 'react';
import { Loader2, Lock, Sparkles } from 'lucide-react';
import { useChatAtlas } from '../../../chat-atlas';
import { useFuncionesAvanzadas } from '../../../hooks/useFuncionesAvanzadas';
import { puedeUsarElAgente } from '../../../lib/planCapabilities';
import { boton } from '../botones';

/** Lo que queda escrito en la caja del chat. Puro, para leerlo entero de un vistazo. */
export function textoParaElAgente(p: {
  cuando: string | null;
  planificacion: string;
  referencias: string;
}): string {
  return [
    `Quiero crear las imágenes para un post de Instagram${p.cuando ? ` que sale el ${p.cuando}` : ''}.`,
    ...(p.planificacion.trim() ? ['', 'Planificación:', p.planificacion.trim()] : []),
    ...(p.referencias.trim() ? ['', 'Referencias:', p.referencias.trim()] : []),
    '',
    '',
  ].join('\n');
}

export function CrearConElAgente({
  cuando,
  planificacion,
  referencias,
  guardarAntes,
  irAlAgente,
}: {
  /** "miércoles 14 de octubre a las 12:00", para que el agente sepa de qué post se habla. */
  cuando: string | null;
  planificacion: string;
  referencias: string;
  /** Guarda el post si tiene cambios. `false` si no se pudo (y la pantalla dice por qué). */
  guardarAntes: () => Promise<boolean>;
  /** Navega a la sección del Agente. */
  irAlAgente: () => void;
}) {
  const chat = useChatAtlas();
  const { plan } = useFuncionesAvanzadas();
  const [yendo, setYendo] = useState(false);
  // Mismo criterio que la caja del calendario: mientras el plan carga, sin candado.
  const bloqueado = plan != null && !puedeUsarElAgente(plan);
  const vacio = !planificacion.trim() && !referencias.trim();

  const llevar = async () => {
    if (vacio || bloqueado || yendo) return;
    setYendo(true);
    const ok = await guardarAntes();
    setYendo(false);
    if (!ok) return;
    // El borrador del chat sobrevive a la navegación: la pantalla del Agente lo muestra en su caja.
    chat.setBorrador(textoParaElAgente({ cuando, planificacion, referencias }));
    irAlAgente();
  };

  const motivo = bloqueado
    ? 'El agente es de los planes Pro y Ultra'
    : vacio
      ? 'Escribí la planificación o pegá referencias'
      : 'Te lleva al Agente con la planificación y las referencias, para crear las imágenes';

  return (
    <button
      type="button"
      onClick={() => void llevar()}
      disabled={vacio || bloqueado || yendo}
      title={motivo}
      className={boton('primario', { chico: true, extra: 'shrink-0 py-2' })}
    >
      {bloqueado ? (
        <Lock className="w-3.5 h-3.5" />
      ) : yendo ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Sparkles className="w-3.5 h-3.5" />
      )}
      Crear con IA
    </button>
  );
}
